import React from "react";
import { Box, Link, Stack, Typography, IconButton } from "@mui/material";

import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

const FOOTER_COLOR = "#123B5D";
const TEXT_MAIN = "#FFFFFF";
const TEXT_MUTED = "rgba(255,255,255,0.72)";
const TEXT_FAINT = "rgba(255,255,255,0.60)";

const BRAND_NAME = "Parivikshak";
const BRAND_URL = "https://parivikshak.in/";

const NAV_LINKS = [
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms of Service", href: "/terms" },
  { label: "Contact", href: "/contact-us" },
];

const SOCIAL_LINKS = [
  {
    label: "Facebook",
    href: "https://facebook.com/parivikshak",
    icon: FacebookIcon,
  },
  {
    label: "Twitter",
    href: "https://twitter.com/parivikshak",
    icon: TwitterIcon,
  },
  {
    label: "LinkedIn",
    href: "https://linkedin.com/company/parivikshak",
    icon: LinkedInIcon,
  },
];

const navLinkSx = {
  color: TEXT_MUTED,
  fontSize: { xs: 12, md: 12.5 },
  lineHeight: 1.5,
  whiteSpace: "nowrap",
  transition: "color .15s",
  "&:hover": {
    color: TEXT_MAIN,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
  },
  "&:focus-visible": {
    outline: "2px solid #FFFFFF",
    outlineOffset: 2,
    borderRadius: "2px",
  },
};

const socialBtnSx = {
  width: 28,
  height: 28,
  color: TEXT_MUTED,
  transition: "color .15s, background-color .15s",
  "&:hover": {
    color: TEXT_MAIN,
    backgroundColor: "rgba(255,255,255,0.10)",
  },
  "&:focus-visible": {
    outline: "2px solid #FFFFFF",
    outlineOffset: 1,
  },
};

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        width: "100%",
        flexShrink: 0,
        boxSizing: "border-box",
        backgroundColor: FOOTER_COLOR,
        backgroundImage: "none",
        color: TEXT_MAIN,
        borderTop: "1px solid rgba(255,255,255,0.10)",
        px: { xs: 1.5, sm: 3, md: 4 },
        py: { xs: 1, md: 0.75 },
        pb: {
          xs: "calc(8px + env(safe-area-inset-bottom, 0px))",
          md: 0.75,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={{ xs: "center", md: "space-between" }}
        flexWrap="wrap"
        columnGap={{ xs: 1.5, sm: 2.5, md: 3 }}
        rowGap={0.5}
        sx={{ width: "100%", maxWidth: 1500, mx: "auto", minHeight: 36 }}
      >
        {/* Copyright */}
        <Typography
          sx={{
            fontSize: { xs: 11.5, md: 12 },
            lineHeight: 1.5,
            color: TEXT_FAINT,
            whiteSpace: "nowrap",
          }}
        >
          &copy; {year}{" "}
          <Link
            href={BRAND_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              color: TEXT_MAIN,
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {BRAND_NAME}
          </Link>
          . All rights reserved.
        </Typography>

        {/* Navigation */}
        <Stack
          component="nav"
          aria-label="Footer"
          direction="row"
          alignItems="center"
          columnGap={{ xs: 1.5, sm: 2.5 }}
          rowGap={0.25}
          flexWrap="wrap"
          justifyContent="center"
        >
          {NAV_LINKS.map((l) => (
            <Link key={l.label} href={l.href} underline="none" sx={navLinkSx}>
              {l.label}
            </Link>
          ))}
        </Stack>

        {/* Social */}
        <Stack direction="row" alignItems="center" spacing={0.25}>
          {SOCIAL_LINKS.map(({ label, href, icon: Icon }) => (
            <IconButton
              key={label}
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              title={label}
              size="small"
              sx={socialBtnSx}
            >
              <Icon sx={{ fontSize: 17 }} />
            </IconButton>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export default Footer;
